import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useId,
} from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Button, Input, Avatar, message, Form, Select, Tooltip } from "antd";
import { AuthContext } from "../context/AuthProvider";
import { useMobileWidgetContext } from "../context/MobileWidgetContext";
import {
  MessageOutlined,
  SendOutlined,
  CloseOutlined,
  BulbOutlined,
  StarOutlined,
  StarFilled,
  BugOutlined,
  ToolOutlined,
  HeartOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { device, useBreakpoints } from "../lib/breakpoints";
import { sendFeedbackForm } from "../helpers/feedback-form";
import { getFeedbackClientContext } from "../helpers/feedback-client-context";
import { trackEvent } from "../helpers/mixpanel";
import Logo from "./general/Logo";

const { TextArea } = Input;
const { Option } = Select;

/** Slate-950 / industrial panel */
const SLATE_950 = "#020617";
const SLATE_900 = "#0f172a";
const SLATE_800 = "#1e293b";
const PANEL_GRID =
  "linear-gradient(rgba(148, 163, 184, 0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.07) 1px, transparent 1px)";

/** Launcher control (screen readers). Avoid internal jargon. */
const FEEDBACK_FAB_ARIA_LABEL = "Give us feedback";

const FEEDBACK_PANEL_TITLE = "Feedback";

/** Portaled Select menu - must live outside styled panel (overflow / stacking). */
const OperatorSignalPortalStyles = createGlobalStyle`
  /* Dark menu shell (beat globals.css white .ant-select-dropdown + z-index) */
  .feedback-operator-dropdown.ant-select-dropdown,
  html body .ant-select-dropdown.feedback-operator-dropdown {
    z-index: 10000006 !important;
    padding: 0 !important;
    border-radius: 0 !important;
    border: 2px solid ${SLATE_800} !important;
    background: ${SLATE_950} !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
  }

  .feedback-operator-dropdown .rc-virtual-list-holder-inner {
    padding: 4px 0 !important;
  }

  /* Default row: light text on dark (beat html body … #1E293B from globals.css) */
  .feedback-operator-dropdown .ant-select-item,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-content {
    color: rgba(248, 250, 252, 0.92) !important;
    border-radius: 0 !important;
    font-weight: 500 !important;
  }

  /* globals.css sets .ant-select-dropdown * { color: #1E293B }; beat it for icons + svg paths */
  .feedback-operator-dropdown .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option .anticon svg,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option .anticon svg path {
    color: rgba(248, 250, 252, 0.88) !important;
    fill: currentColor !important;
  }

  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item:hover,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option:hover {
    background: rgba(148, 163, 184, 0.12) !important;
    color: #ffffff !important;
  }

  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option:hover
    .ant-select-item-option-content,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option:hover span {
    color: #ffffff !important;
  }

  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option:hover .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option:hover .anticon svg,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option:hover .anticon svg path {
    color: #ffffff !important;
    fill: currentColor !important;
  }

  /* Hover / keyboard highlight + selected: high contrast on accent wash */
  .feedback-operator-dropdown .ant-select-item-option-active,
  .feedback-operator-dropdown .ant-select-item-option-selected,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-active,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected {
    background: rgba(255, 87, 34, 0.32) !important;
    color: #ffffff !important;
  }

  .feedback-operator-dropdown .ant-select-item-option-active .ant-select-item-option-content,
  .feedback-operator-dropdown .ant-select-item-option-selected .ant-select-item-option-content,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-active
    .ant-select-item-option-content,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected
    .ant-select-item-option-content,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-active span,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected span {
    color: #ffffff !important;
  }

  .feedback-operator-dropdown .ant-select-item-option-active .anticon,
  .feedback-operator-dropdown .ant-select-item-option-selected .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-active .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected .anticon,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-active .anticon svg,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected .anticon svg,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-active .anticon svg path,
  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected .anticon svg path {
    color: #ffffff !important;
    fill: currentColor !important;
  }

  html body .ant-select-dropdown.feedback-operator-dropdown .ant-select-item-option-selected {
    font-weight: 700 !important;
  }
`;

/**
 * Icon-based stars instead of antd `Rate`: globals.css and other rules often
 * force `.anticon` / `svg` to small fixed sizes; explicit px + !important in
 * `StarRateWrap` keeps tap targets large and predictable.
 */
function FeedbackStarRow({
  value,
  onChange,
  sizePx = 44,
  tooltips = [],
  captionId,
}) {
  const [hoverValue, setHoverValue] = useState(null);
  const rowRef = useRef(null);

  const filled = Number(value) || 0;
  const preview = hoverValue != null ? hoverValue : filled;

  const handleClick = (n) => {
    setHoverValue(null);
    if (filled === n) {
      onChange?.(undefined);
    } else {
      onChange?.(n);
    }
  };

  const handleRowPointerMove = (e) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const star = el?.closest?.("[data-feedback-star]");
    if (star && rowRef.current?.contains(star)) {
      const n = Number(star.getAttribute("data-feedback-star"));
      if (n >= 1 && n <= 5) setHoverValue(n);
    }
  };

  const handleRowPointerLeave = (e) => {
    const next = e.relatedTarget;
    if (next && rowRef.current?.contains(next)) return;
    setHoverValue(null);
  };

  return (
    <StarRateWrap style={{ "--feedback-star-px": `${sizePx}px` }}>
      <div
        ref={rowRef}
        className="feedback-star-row-inner"
        role="radiogroup"
        aria-labelledby={captionId}
        aria-valuenow={filled}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuetext={
          filled ? `${filled} of 5 stars` : "No rating selected"
        }
        onPointerMove={handleRowPointerMove}
        onPointerLeave={handleRowPointerLeave}
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const active = n <= preview;
          return (
            <button
              key={n}
              type="button"
              data-feedback-star={n}
              title={tooltips[n - 1]}
              aria-label={
                tooltips[n - 1]
                  ? `${tooltips[n - 1]}, ${n} of 5`
                  : `Rate ${n} of 5`
              }
              onPointerEnter={() => setHoverValue(n)}
              onClick={() => handleClick(n)}
              className="feedback-star-btn"
            >
              {active ? (
                <StarFilled className="feedback-star-glyph feedback-star-glyph--filled" />
              ) : (
                <StarOutlined className="feedback-star-glyph feedback-star-glyph--empty" />
              )}
            </button>
          );
        })}
      </div>
    </StarRateWrap>
  );
}

function FeedbackRatingField({ value, onChange, sizePx, tooltips }) {
  const captionId = useId();

  return (
    <div className="feedback-rating-field">
      <p className="feedback-rating-caption" id={captionId}>
        How would you rate your experience?
      </p>
      <FeedbackStarRow
        captionId={captionId}
        value={value}
        onChange={onChange}
        sizePx={sizePx}
        tooltips={tooltips}
      />
    </div>
  );
}

const FeedbackWidget = ({
  isVisible = true,
  isOpen: externalIsOpen,
  onToggle,
}) => {
  const { user, userInfo } = useContext(AuthContext);
  const { isMobile } = useBreakpoints();
  const { isAIWidgetActive, setActiveWidget, clearActiveWidget } =
    useMobileWidgetContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullyOpen, setIsFullyOpen] = useState(false);
  const [isLoadingFading, setIsLoadingFading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // Use external state if provided, otherwise use internal state
  const currentIsOpen = externalIsOpen !== undefined ? externalIsOpen : isOpen;

  // Sync internal state with external state
  React.useEffect(() => {
    if (externalIsOpen !== undefined) {
      if (externalIsOpen && !isOpen) {
        // Opening the widget
        setIsOpen(true);
        setIsFullyOpen(false);
        setIsLoadingFading(false);

        // Wait for scale animation to complete, then start fade-out
        setTimeout(() => {
          setIsLoadingFading(true);
          // Show content after fade-out animation completes
          setTimeout(() => {
            setIsFullyOpen(true);
            // Focus on first input
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          }, 200);
        }, 250);
      } else if (!externalIsOpen && isOpen) {
        // Closing the widget
        setIsOpen(false);
        setIsFullyOpen(false);
        setIsLoadingFading(false);
        setSuccessfullySubmitted(false);
        form.resetFields();
      }
    }
  }, [externalIsOpen, isOpen, form]);

  const feedbackTypes = [
    {
      value: "feature-request",
      label: "Feature Request",
      icon: <BulbOutlined />,
    },
    { value: "bug-report", label: "Bug Report", icon: <BugOutlined /> },
    { value: "improvement", label: "Improvement", icon: <ToolOutlined /> },
    {
      value: "general-feedback",
      label: "General Feedback",
      icon: <MessageOutlined />,
    },
    { value: "praise", label: "Praise", icon: <HeartOutlined /> },
    { value: "other", label: "Other", icon: <ThunderboltOutlined /> },
  ];

  const toggleWidget = () => {
    if (onToggle) {
      // Use external toggle if provided
      onToggle();
    } else {
      // Use internal state management
      if (!isOpen) {
        // Opening the widget
        setIsOpen(true);
        setIsFullyOpen(false);
        setIsLoadingFading(false);

        // Set this widget as active on mobile
        if (isMobile) {
          setActiveWidget("feedback");
        }

        // Wait for scale animation to complete, then start fade-out
        setTimeout(() => {
          setIsLoadingFading(true);
          // Show content after fade-out animation completes
          setTimeout(() => {
            setIsFullyOpen(true);
            // Focus on first input
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
          }, 200);
        }, 250);
      } else {
        // Closing the widget
        setIsOpen(false);
        setIsFullyOpen(false);
        setIsLoadingFading(false);
        setSuccessfullySubmitted(false);
        form.resetFields();

        // Clear active widget on mobile
        if (isMobile) {
          clearActiveWidget();
        }
      }
    }
  };

  const scrollFormToFirstError = (errorInfo) => {
    const first = errorInfo?.errorFields?.[0];
    const name = first?.name;
    if (name != null) {
      form.scrollToField(name, {
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const clientCtx = await getFeedbackClientContext();

      trackEvent("Feedback Widget Submitted", {
        feedback_type: values.feedbackType,
        has_rating: !!values.rating,
        message_length: values.message?.length || 0,
        user_logged_in: !!user,
        device_type: isMobile ? "mobile" : "desktop",
        has_approx_location: !!(
          clientCtx.geo?.country || clientCtx.geo?.region
        ),
      });

      // Prepare data for API. Use Firebase user when Firestore userInfo is still loading.
      const resolvedEmail =
        userInfo?.email || user?.email || values.email || "";
      const resolvedName =
        userInfo?.firstName && userInfo?.lastName
          ? `${userInfo.firstName} ${userInfo.lastName}`
          : userInfo?.displayName ||
            user?.displayName ||
            values.name ||
            "Anonymous";

      const rawRating = values.rating;
      let ratingOut;
      if (rawRating === undefined || rawRating === null || rawRating === "") {
        ratingOut = undefined;
      } else {
        const n = Number(rawRating);
        ratingOut = Number.isFinite(n) ? n : undefined;
      }

      const feedbackData = {
        name: resolvedName,
        email: resolvedEmail,
        feedbackType: values.feedbackType,
        rating: ratingOut,
        message: values.message,
        userId: user?.uid || "",
        userEmail: resolvedEmail,
        deviceType: isMobile ? "mobile" : "desktop",
        userAgent: clientCtx.userAgent,
        browserLabel: clientCtx.browserLabel,
        locale: clientCtx.locale,
        ...(clientCtx.geo ? { geo: clientCtx.geo } : {}),
      };

      // Submit via feedback form API
      const result = await sendFeedbackForm(feedbackData);

      if (result.success) {
        setSuccessfullySubmitted(true);

        trackEvent("Feedback Widget Success", {
          feedback_type: values.feedbackType,
          rating: values.rating,
        });

        // Reset form after success
        setTimeout(() => {
          form.resetFields();
          setSuccessfullySubmitted(false);
        }, 10000);
      } else {
        // Show specific error message from backend if available
        const errorMessage =
          result.message || "Failed to submit feedback. Please try again.";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      // Show the actual error message to the user
      const displayMessage =
        error.message ||
        "Sorry, there was an error submitting your feedback. Please try again.";
      message.error(displayMessage);

      trackEvent("Feedback Widget Error", {
        error: error.message,
        feedback_type: values.feedbackType,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <WidgetContainer>
      {currentIsOpen ? <OperatorSignalPortalStyles /> : null}
      {/* Floating Button - hidden on mobile when AI widget is active or when this widget is open */}
      {(isMobile ? !isAIWidgetActive && !currentIsOpen : true) && (
        <FloatingButton
          type="default"
          size="large"
          aria-label={
            currentIsOpen ? "Close feedback" : FEEDBACK_FAB_ARIA_LABEL
          }
          icon={
            currentIsOpen ? (
              <CloseOutlined className="operator-signal-fab-icon" />
            ) : (
              <ToolOutlined className="operator-signal-fab-icon" />
            )
          }
          onClick={toggleWidget}
          className="feedback-widget-button operator-signal-fab"
        />
      )}

      {/* Feedback Widget */}
      {currentIsOpen && (
        <FeedbackWidgetContainer className="feedback-widget-container">
          {/* Loading Screen */}
          {!isFullyOpen && (
            <LoadingScreen className={isLoadingFading ? "fade-out" : ""}>
              <LogoContainer>
                <LoadingSignalIcon />
              </LogoContainer>
            </LoadingScreen>
          )}

          {/* Feedback Content */}
          <FeedbackContent className={isFullyOpen ? "visible" : "hidden"}>
            <FeedbackHeader>
              <Avatar
                shape="square"
                size={48}
                icon={
                  <ToolOutlined style={{ filter: "brightness(0) invert(1)" }} />
                }
                style={{
                  backgroundColor: SLATE_800,
                  border: "2px solid rgba(255, 87, 34, 0.65)",
                  borderRadius: 0,
                  marginRight: "12px",
                }}
              />
              <FeedbackTitleBlock>
                <FeedbackTitle>{FEEDBACK_PANEL_TITLE}</FeedbackTitle>
              </FeedbackTitleBlock>
              {/* Mobile close button */}
              <MobileCloseButton onClick={toggleWidget}>
                <CloseOutlined style={{ filter: "brightness(0) invert(1)" }} />
              </MobileCloseButton>
            </FeedbackHeader>

            <FeedbackBody>
              {successfullySubmitted ? (
                <SuccessMessage>
                  <SuccessLogoWrap>
                    <Logo
                      displayMode="icon"
                      linkTo={null}
                      size="large"
                      color="white"
                    />
                  </SuccessLogoWrap>
                  <h3>Thank you</h3>
                  <p>We received your feedback.</p>
                </SuccessMessage>
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  onFinishFailed={scrollFormToFirstError}
                  size="large"
                >
                  <Form.Item
                    className="feedback-rating-item"
                    name="rating"
                    colon={false}
                    rules={[
                      {
                        required: true,
                        message: "Please tap a star rating",
                      },
                    ]}
                  >
                    <FeedbackRatingField
                      sizePx={isMobile ? 52 : 44}
                      tooltips={[
                        "Poor",
                        "Fair",
                        "Good",
                        "Great",
                        "Excellent",
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="feedbackType"
                    label="What kind of feedback?"
                    rules={[
                      {
                        required: true,
                        message: "Please select a feedback type",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select category"
                      ref={inputRef}
                      allowClear
                      getPopupContainer={() => document.body}
                      popupClassName="feedback-operator-dropdown"
                    >
                      {feedbackTypes.map((type) => (
                        <Option key={type.value} value={type.value}>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {type.icon}
                            {type.label}
                          </span>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {!user && (
                    <>
                      <Form.Item name="name" label="Your Name (Optional)">
                        <Input placeholder="Enter your name" />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label="Your Email (Optional)"
                        rules={[
                          {
                            validator: (_, value) => {
                              const v =
                                value === undefined || value === null
                                  ? ""
                                  : String(value).trim();
                              if (!v) return Promise.resolve();
                              const ok =
                                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
                              if (!ok) {
                                return Promise.reject(
                                  new Error("Please enter a valid email")
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input placeholder="Enter your email" type="email" />
                      </Form.Item>
                    </>
                  )}

                  <Form.Item
                    name="message"
                    label="Tell us more"
                    rules={[
                      { required: true, message: "Please share your feedback" },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="What went wrong, what would help, or what you liked. Anything that helps us improve."
                      maxLength={1000}
                    />
                  </Form.Item>

                  <Form.Item>
                    <SubmitButton
                      type="primary"
                      htmlType="submit"
                      loading={isSubmitting}
                      icon={<SendOutlined style={{ color: "#ffffff" }} />}
                    >
                      Send feedback
                    </SubmitButton>
                  </Form.Item>
                </Form>
              )}
            </FeedbackBody>
          </FeedbackContent>
        </FeedbackWidgetContainer>
      )}
    </WidgetContainer>
  );
};

export default FeedbackWidget;

// Styled Components
const WidgetContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  z-index: 10000002 !important;
  pointer-events: auto;
  animation: slideInLeft 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @media ${device.mobile} {
    bottom: 2rem;
    left: 2rem;
  }

  @keyframes slideInLeft {
    0% {
      opacity: 0;
      transform: translateX(-30px) scale(0.8);
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
`;

const FloatingButton = styled(Button)`
  width: 68px !important;
  height: 68px !important;
  min-width: 68px !important;
  min-height: 68px !important;
  max-width: 68px !important;
  max-height: 68px !important;
  border-radius: 0 !important;
  background: ${SLATE_950} !important;
  color: var(--color-text-white) !important;
  border: 1px solid var(--color-action) !important;
  box-shadow: none;
  position: relative;
  overflow: hidden !important;
  padding: 0 !important;
  line-height: 1 !important;

  .operator-signal-fab-icon.anticon {
    font-size: 22px !important;
    color: var(--color-text-white) !important;
  }

  .operator-signal-fab-icon.anticon svg {
    width: 22px !important;
    height: 22px !important;
    color: var(--color-text-white) !important;
    fill: currentColor !important;
  }

  &:hover {
    background: #000000 !important;
    border-color: var(--color-action) !important;
    box-shadow:
      0 0 0 1px rgba(255, 87, 34, 0.45),
      0 0 28px rgba(255, 87, 34, 0.5),
      0 0 52px rgba(255, 87, 34, 0.22);
  }

  &:hover .operator-signal-fab-icon.anticon,
  &:hover .operator-signal-fab-icon.anticon svg {
    color: var(--color-action) !important;
    fill: var(--color-action) !important;
  }

  &:active {
    transform: scale(0.98);
    box-shadow:
      0 0 0 1px rgba(255, 87, 34, 0.35),
      0 0 18px rgba(255, 87, 34, 0.4);
  }

  &:focus-visible {
    outline: 2px solid var(--color-action);
    outline-offset: 2px;
  }

  @media ${device.mobile} {
    width: 56px !important;
    height: 56px !important;
    min-width: 56px !important;
    min-height: 56px !important;
    max-width: 56px !important;
    max-height: 56px !important;

    .operator-signal-fab-icon.anticon {
      font-size: 19px !important;
    }

    .operator-signal-fab-icon.anticon svg {
      width: 19px !important;
      height: 19px !important;
    }
  }
`;

const FeedbackWidgetContainer = styled.div`
  position: absolute;
  bottom: 80px;
  left: 0;
  width: 450px;
  height: 600px;
  background-color: ${SLATE_950};
  background-image: ${PANEL_GRID};
  background-size: 24px 24px;
  border-radius: 0;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  border: 2px solid ${SLATE_800};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 10000002 !important;

  animation: scaleOpen 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform-origin: bottom left;

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-group,
  .ant-select-selector,
  .ant-input-number {
    background: rgba(15, 23, 42, 0.65) !important;
    color: rgba(248, 250, 252, 0.96) !important;
    border: 2px solid ${SLATE_800} !important;
    border-radius: 0 !important;
    outline: none !important;
    box-shadow: none !important;

    &::placeholder {
      color: rgba(248, 250, 252, 0.42) !important;
    }

    &:focus,
    &:focus-within,
    &:focus-visible,
    &:hover {
      border-color: var(--color-action) !important;
      box-shadow: 0 0 0 1px rgba(255, 87, 34, 0.35) !important;
      outline: none !important;
    }
  }

  .ant-input::placeholder,
  textarea.ant-input::placeholder {
    color: rgba(248, 250, 252, 0.42) !important;
  }

  .ant-select-selection-placeholder {
    color: rgba(248, 250, 252, 0.45) !important;
  }

  .ant-select-selection-item {
    color: rgba(248, 250, 252, 0.95) !important;
  }

  .ant-select-arrow .anticon {
    color: rgba(248, 250, 252, 0.75) !important;
  }

  .ant-select,
  .ant-select-selector {
    position: relative !important;
  }

  .ant-select-selector {
    display: flex !important;
    align-items: center !important;
    min-height: 48px !important;
    /* Room for clear + chevron (globals + nested padding must not collapse this) */
    padding-right: 2.75rem !important;
  }

  .ant-select.ant-select-allow-clear .ant-select-selector {
    padding-right: 3.25rem !important;
  }

  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    right: 10px !important;
    inset-inline-end: 10px !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    pointer-events: none !important;

    .anticon {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
    }
  }

  .ant-select-clear {
    position: absolute !important;
    top: 50% !important;
    right: 2.1rem !important;
    inset-inline-end: 2.1rem !important;
    transform: translateY(-50%) !important;
    width: 1.35rem !important;
    height: 1.35rem !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 9999px !important;
    background: rgba(15, 23, 42, 0.55) !important;
    color: rgba(248, 250, 252, 0.75) !important;
    font-size: 0.65rem !important;
    z-index: 2 !important;
  }

  .ant-select-clear:hover {
    color: rgba(248, 250, 252, 0.95) !important;
    background: rgba(30, 41, 59, 0.85) !important;
  }

  /* Beat globals.css rule that forces dark text on .ant-select-selection-item */
  html body & .ant-select .ant-select-selection-item,
  html body & .ant-select .ant-select-selection-item span,
  html body & .ant-select .ant-select-selection-item .anticon {
    color: rgba(248, 250, 252, 0.96) !important;
  }

  /* globals.css uses .ant-select * { color: #1E293B }; icons need explicit white + svg fill */
  html body & .ant-select .ant-select-selection-overflow .anticon,
  html body & .ant-select .ant-select-selection-item .anticon,
  & .ant-select .ant-select-selection-overflow .anticon,
  & .ant-select .ant-select-selection-item .anticon {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  html body & .ant-select .ant-select-selection-overflow .anticon svg,
  html body & .ant-select .ant-select-selection-item .anticon svg,
  & .ant-select .ant-select-selection-overflow .anticon svg,
  & .ant-select .ant-select-selection-item .anticon svg {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  html body & .ant-select .ant-select-selection-overflow .anticon svg path,
  html body & .ant-select .ant-select-selection-item .anticon svg path,
  & .ant-select .ant-select-selection-overflow .anticon svg path,
  & .ant-select .ant-select-selection-item .anticon svg path {
    fill: currentColor !important;
  }

  .ant-form-item-label > label {
    font-weight: 600;
    color: rgba(248, 250, 252, 0.9) !important;
  }

  .ant-form-item-explain-error {
    color: #ff8a65 !important;
  }

  .ant-input-textarea-show-count::after {
    color: rgba(148, 163, 184, 0.85) !important;
  }

  .ant-input-clear-icon,
  .ant-input-textarea-clear-icon {
    color: rgba(248, 250, 252, 0.55) !important;
  }

  .ant-select-selection-item .anticon {
    color: rgba(248, 250, 252, 0.88) !important;
  }

  @media ${device.mobile} {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    border-radius: 0;
    position: fixed;
    z-index: 10000002 !important;
    box-shadow: none;
    border-width: 0;
    border-bottom: 2px solid ${SLATE_800};

    @supports not (height: 100dvh) {
      height: calc(100vh - env(keyboard-inset-height, 0px));
    }
  }

  @keyframes scaleOpen {
    0% {
      opacity: 0;
      transform: scale(0.96) translateY(6px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const LoadingScreen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${SLATE_950};
  background-image: ${PANEL_GRID};
  background-size: 24px 24px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: opacity 0.3s ease;

  &.fade-out {
    opacity: 0;
    pointer-events: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: logoPulse 2s ease-in-out infinite;

  @keyframes logoPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

// Loading glyph: globals can shrink `.anticon` - force size + Safety Orange signal.
const LoadingSignalIcon = styled(ToolOutlined)`
  && {
    font-size: 120px;
    color: var(--color-action) !important;
    filter: none;
  }

  && svg {
    color: var(--color-action) !important;
    fill: var(--color-action) !important;
  }
`;

const FeedbackContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: opacity 0.3s ease;

  &.visible {
    opacity: 1;
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;

const FeedbackHeader = styled.div`
  background: ${SLATE_900};
  color: rgba(248, 250, 252, 0.98);
  padding: 1.35rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: 0;
  position: relative;
  border-bottom: 2px solid ${SLATE_800};
  flex-shrink: 0;

  @media ${device.mobile} {
    padding: 1.25rem 3.25rem 1.25rem 1.1rem;
  }
`;

/** Fills space next to avatar; keeps title vertically centered with the 48px icon. */
const FeedbackTitleBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 48px;
`;

const FeedbackTitle = styled.h3`
  margin: 0;
  font-family: var(--font-primary);
  font-size: clamp(1.2rem, 4vw, 1.65rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.15;
  color: white;
  text-align: left;
  text-transform: uppercase;
`;

const MobileCloseButton = styled(Button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${SLATE_950} !important;
  border: 2px solid rgba(255, 87, 34, 0.55) !important;
  border-radius: 0 !important;
  color: rgba(248, 250, 252, 0.95) !important;
  width: 36px !important;
  height: 36px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  &:hover {
    border-color: var(--color-action) !important;
    color: var(--color-action) !important;
  }

  @media (min-width: 769px) {
    display: none !important;
  }
`;

const FeedbackBody = styled.div`
  flex: 1;
  padding: 1.5rem 1.35rem 1.35rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.2);

  .ant-form-item {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ant-form-item-label {
    padding-bottom: 0.2rem;

    > label {
      font-weight: 600;
      color: rgba(248, 250, 252, 0.88) !important;
      font-size: 0.8125rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;

      &.ant-form-item-required::before {
        color: var(--color-action) !important;
        margin-right: 0.25rem;
      }
    }
  }

  .feedback-rating-field {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    width: 100%;
    padding: 0.35rem 0 0.5rem;
  }

  .feedback-rating-caption {
    margin: 0;
    padding: 0;
    font-weight: 700;
    color: rgba(248, 250, 252, 0.82);
    font-size: 0.75rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.35;
    font-family: var(--font-mono);
  }

  .ant-input {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .ant-select {
    line-height: 1 !important;
  }

  .ant-select-selector {
    /* Keep right gutter for suffix + clear; do not use symmetric padding shorthand */
    padding: 0.35rem 3.25rem 0.35rem 0.85rem !important;
    min-height: 44px !important;
    display: flex;
    align-items: center;
  }

  .ant-select.ant-select-allow-clear .ant-select-selector {
    padding-right: 3.75rem !important;
  }

  .ant-select-selection-search {
    height: 100% !important;
  }

  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
    display: flex !important;
    align-items: center !important;
    color: rgba(248, 250, 252, 0.96) !important;
  }

  .ant-select-selection-item span {
    color: inherit !important;
  }

  /* Selected value row: label text stays light; wrench/etc. must stay white (beats globals .ant-select *) */
  html body & .ant-select .ant-select-selection-overflow .anticon,
  html body & .ant-select .ant-select-selection-item .anticon,
  & .ant-select .ant-select-selection-overflow .anticon,
  & .ant-select .ant-select-selection-item .anticon {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  html body & .ant-select .ant-select-selection-overflow .anticon svg,
  html body & .ant-select .ant-select-selection-item .anticon svg,
  & .ant-select .ant-select-selection-overflow .anticon svg,
  & .ant-select .ant-select-selection-item .anticon svg {
    color: #ffffff !important;
    fill: currentColor !important;
  }
  html body & .ant-select .ant-select-selection-overflow .anticon svg path,
  html body & .ant-select .ant-select-selection-item .anticon svg path,
  & .ant-select .ant-select-selection-overflow .anticon svg path,
  & .ant-select .ant-select-selection-item .anticon svg path {
    fill: currentColor !important;
  }

  .ant-input-textarea-count {
    margin-top: 0.65rem !important;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${SLATE_800};
    border-radius: 0;

    &:hover {
      background: rgba(255, 87, 34, 0.45);
    }
  }
`;

const SuccessLogoWrap = styled.div`
  margin-bottom: 1rem;

  img {
    display: block;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 2rem;

  h3 {
    color: rgba(248, 250, 252, 0.98);
    margin-bottom: 0.5rem;
    font-family: var(--font-mono);
    font-weight: 800;
    font-size: 1.35rem;
    letter-spacing: 0.14em;
  }

  p {
    color: rgba(203, 213, 225, 0.92);
    line-height: 1.55;
    max-width: 300px;
    margin: 0;
    font-size: 0.95rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: 48px;
  background: var(--color-action) !important;
  color: #ffffff !important;
  border: 2px solid #ffffff !important;
  font-weight: 800;
  font-size: 0.8125rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border-radius: 0 !important;
  margin-top: 0.5rem;
  box-shadow: none;
  transition: transform 0.12s ease, filter 0.12s ease;

  &:hover:not(:disabled):not(.ant-btn-loading) {
    background: var(--color-action-hover) !important;
    color: #ffffff !important;
    filter: brightness(1.02);
    transform: none;
    box-shadow: none;
  }

  &:active:not(:disabled):not(.ant-btn-loading) {
    transform: scale(0.99);
    box-shadow: none;
  }

  .anticon {
    font-size: 0.9rem;
  }
`;

const StarRateWrap = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0.2rem 0 0;

  .feedback-star-row-inner {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: clamp(8px, 2vw, 14px);
    touch-action: pan-y;
  }

  .feedback-star-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 6px 4px;
    line-height: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0;
    touch-action: manipulation;
  }

  .feedback-star-btn:focus-visible {
    outline: 2px solid var(--color-action);
    outline-offset: 2px;
  }

  /* Match LoadingStarIcon workaround: globals can shrink .anticon svg - force px. */
  .feedback-star-glyph.anticon {
    font-size: var(--feedback-star-px, 44px) !important;
    line-height: 1 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }

  .feedback-star-glyph.anticon svg {
    width: var(--feedback-star-px, 44px) !important;
    height: var(--feedback-star-px, 44px) !important;
  }

  .feedback-star-glyph--empty.anticon {
    color: rgba(148, 163, 184, 0.45) !important;
  }

  .feedback-star-glyph--empty.anticon svg {
    fill: currentColor !important;
  }

  .feedback-star-glyph--filled.anticon {
    color: var(--color-action) !important;
  }

  .feedback-star-glyph--filled.anticon svg {
    fill: var(--color-action) !important;
  }

  @media ${device.mobile} {
    .feedback-star-glyph.anticon {
      font-size: var(--feedback-star-px, 52px) !important;
    }

    .feedback-star-glyph.anticon svg {
      width: var(--feedback-star-px, 52px) !important;
      height: var(--feedback-star-px, 52px) !important;
    }
  }
`;
